import Section from "../../components/base/Section.jsx";
import Radio from "../../components/base/Radio.jsx";
import FileUpload from "../../components/base/FileUpload.jsx";
import FileUploadLicense from "../../components/FileUploadLicense.jsx";
import RegistrationOption from "../../components/RegistrationOption.jsx";
import { useState, useEffect } from "react";
import { theme } from "../../theme.js";

const API_URL = process.env.REACT_APP_API_URL;

export default function Step3({ formData, setFormData, requirements, options, corrals, isEditMode = false, isReadOnly = false}) {
  const [acceptedOptions, setAcceptedOptions] = useState({});
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Initialize existing options, documents, and corral when in edit mode
  useEffect(() => {
    if (isEditMode) {
      // Initialize existing options
      if (formData?.existingOptions) {
        const existingOptionIds = {};
        const existingExtraOptions = {};

        Object.entries(formData.existingOptions).forEach(([optionId, optionData]) => {
          existingOptionIds[optionId] = true;
          existingExtraOptions[optionId] = optionData;
        });

        setAcceptedOptions(existingOptionIds);
        setFormData((prev) => ({
          ...prev,
          extraOptions: {
            ...(prev?.extraOptions || {}),
            ...existingExtraOptions,
          },
        }));
        console.log("Initialized existing options for edit mode:", existingOptionIds);
      }

      // Initialize existing documents
      if (formData?.existingDocuments) {
        const existingDocs = {};
        Object.entries(formData.existingDocuments).forEach(([docId, docData]) => {
          existingDocs[docId] = docData;
        });

        setFormData((prev) => ({
          ...prev,
          documents: {
            ...(prev?.documents || {}),
            ...existingDocs,
          },
        }));
        console.log("Initialized existing documents for edit mode:", existingDocs);
      }

      // Initialize existing corral
      if (formData?.existingCorral) {
        // Only set if not already set to avoid overwriting user changes
        setFormData((prev) => ({
          ...prev,
          corrals: prev?.corrals || formData.existingCorral.name,
        }));
        console.log("Initialized existing corral for edit mode:", formData.existingCorral.name);
      }
    }
  }, [isEditMode, formData?.existingOptions, formData?.existingDocuments, formData?.existingCorral]);

  const toggleOptionAcceptance = (option) => {
    const id = option.id_bdd || option.id || option.name;
    setAcceptedOptions((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));

    setFormData((prev) => {
      const prevExtra = (prev && prev.extraOptions) ? { ...prev.extraOptions } : {};
      const isNowAccepted = !acceptedOptions[id];

      if (isNowAccepted) {
        // add option to extraOptions
        if (option.type === "boolean") {
          prevExtra[id] = { id: id, name: option.name, price: option.price || 0, value: 1 };
        } else {
          // enum or other: add placeholder; radio will set actual choice
          prevExtra[id] = { id: id, name: option.name, price: option.price || 0, value: null };
        }
      } else {
        // remove option from extraOptions
        if (prevExtra.hasOwnProperty(id)) delete prevExtra[id];
      }

      return {
        ...(prev || {}),
        extraOptions: prevExtra,
      };
    });
  };


  return (
    <>
      {/* Documents obligatoires */}
      {requirements.length > 0 && (
        <Section title="DOCUMENTS OBLIGATOIRES À SOUMETTRE">
          <div style={{ ...styles.requirementsContainer, gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr" }}>
            <div style={styles.licenseRequirementsBox}>
              {requirements.map((requirement) => {
                // Check if it's a license requirement
                if (requirement.type === "license") {
                  return (
                    <FileUploadLicense
                      key={requirement.id_bdd}
                      licenseRequirement={requirement}
                      required={true}
                      uploadUrl={`${API_URL}/api/uploads`}
                      initialValues={formData?.licenseDocuments?.[requirement.id_bdd] || null}
                      existingValues={formData?.existingDocuments?.[String(requirement.id_bdd)] || null}
                      onExistingDeleted={(existingFileData) => {
                        setFormData((prev) => {
                          const deleted = [...(prev?.deletedDocuments || [])];
                          if (existingFileData?.id && !deleted.includes(existingFileData.id)) deleted.push(existingFileData.id);
                          return { ...(prev || {}), deletedDocuments: deleted };
                        });
                      }}
                      onUploaded={(docs) => {
                        setFormData((prev) => {
                          const licenseData = prev?.licenseDocuments ? { ...prev.licenseDocuments } : {};
                          licenseData[requirement.id_bdd] = docs;
                          return { ...(prev || {}), licenseDocuments: licenseData };
                        });
                      }}
                      onChange={(file) => {
                        if (!file) {
                          setFormData((prev) => {
                            const licenseData = prev?.licenseDocuments ? { ...prev.licenseDocuments } : {};
                            delete licenseData[requirement.id_bdd];
                            return { ...(prev || {}), licenseDocuments: licenseData };
                          });
                        }
                      }}
                    />
                  );
                }
                return null;
              })}
            </div>

            <div style={styles.otherRequirementsBox}>
              {requirements.map((requirement) => {
                // Check if it's NOT a license requirement
                if (requirement.type !== "license") {
                  return (
                    <FileUpload
                      key={requirement.id_bdd}
                      requirement={requirement}
                      accept="application/pdf,image/*"
                      uploadImmediately={true}
                      uploadUrl={`${API_URL}/api/uploads`}
                      initialValue={formData?.documents?.[requirement.id_bdd] || null}
                      existingFile={formData?.existingDocuments?.[String(requirement.id_bdd)] || null}
                      onUploaded={(json) => {
                        setFormData((prev) => {
                          const docs = prev?.documents ? { ...prev.documents } : {};
                          if (json) docs[requirement.id_bdd] = { id: requirement.id_bdd, name: requirement.name, file_id: json.id, path: json.path, filename: json.filename };
                          return { ...(prev || {}), documents: docs };
                        });
                      }}
                      onExistingDeleted={(existingFileData) => {
                        setFormData((prev) => {
                          const deleted = [...(prev?.deletedDocuments || [])];
                          if (existingFileData?.id && !deleted.includes(existingFileData.id)) deleted.push(existingFileData.id);
                          return { ...(prev || {}), deletedDocuments: deleted };
                        });
                      }}
                      onChange={(file) => {
                        // keep local UI state if needed; uploaded file handled in onUploaded
                        if (!file) {
                          setFormData((prev) => {
                            const docs = prev?.documents ? { ...prev.documents } : {};
                            delete docs[requirement.id_bdd];
                            return { ...(prev || {}), documents: docs };
                          });
                        }
                      }}
                    />
                  );
                }
                return null;
              })}
            </div>
          </div>
        </Section>
      )}
    {/* Options de la course */}
      <Section title="OPTIONS">
        {options.map((option) => (
          <RegistrationOption
            key={option.id || option.name}
            option={option}
            isEditMode={isEditMode}
            formData={formData}
            setFormData={setFormData}
            acceptedOptions={acceptedOptions}
            onToggle={toggleOptionAcceptance}
          />
        ))}
      </Section>
      {(corrals.length > 0) &&
      <Section title="SAS">
        <Radio
          choice={{ name: "corrals", label: "Choisissez votre sas de départ", choices: corrals }}
          formData={formData}
          setFormData={setFormData}
        />
      </Section>
      }
    </>
  );
}

const styles = {
  requirementsContainer: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: theme.spacing.lg,
  },
  licenseRequirementsBox: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing.lg,
  },
  otherRequirementsBox: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing.lg,
  },
};
